"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DnpColumnCandidateFinder = exports.dnpColumnStateSchema = void 0;
const zod_1 = require("zod");
const dnp_metadata_value_1 = require("./dnp-metadata-value");
exports.dnpColumnStateSchema = zod_1.z.object({
    value: zod_1.z.string(),
    count: zod_1.z.number().int().nonnegative(),
});
class DnpColumnCandidateFinder {
    static { this.MARKER_EXAMPLE_LIMIT = 5; }
    static { this.PARSER_KEY_PREFIXES = [
        'orcad.',
        'altium.',
        'kicad.',
        'allegro.',
        'eagle.',
    ]; }
    static { this.STRONG_NAME_PATTERN = /dnp|dni|dnf|dns|nopop|populat|fitted|nofit|stuff|install|placed|placement/; }
    static { this.WEAK_NAME_PATTERN = /bom|exclude|ignore|assembly|variant|fit/; }
    static { this.FLAG_VALUES = new Set([
        '@',
        'x',
        '*',
        '-',
        '+',
        'y',
        'n',
        'yes',
        'no',
        'true',
        'false',
        '0',
        '1',
    ]); }
    static find(metadataRows) {
        const valueCounts = DnpColumnCandidateFinder.countValues(metadataRows);
        const flagColumns = [];
        const markerColumns = [];
        for (const [column, counts] of valueCounts) {
            const presentCount = [...counts.values()].reduce((sum, count) => sum + count, 0);
            const absentCount = metadataRows.length - presentCount;
            const stateCount = counts.size + (absentCount > 0 ? 1 : 0);
            if (counts.size <= 2 && stateCount >= 2 && stateCount <= 3) {
                flagColumns.push({
                    column,
                    states: DnpColumnCandidateFinder.sortStates(counts),
                    absentCount,
                    score: DnpColumnCandidateFinder.scoreFlagColumn(column, [...counts.keys()]),
                });
                continue;
            }
            const marked = [...counts].filter(([value]) => dnp_metadata_value_1.DnpMetadataValue.isMarker(value));
            if (marked.length > 0) {
                markerColumns.push({
                    column,
                    markerCount: marked.reduce((sum, [, count]) => sum + count, 0),
                    presentCount,
                    markerExamples: DnpColumnCandidateFinder.sortStates(new Map(marked)).slice(0, DnpColumnCandidateFinder.MARKER_EXAMPLE_LIMIT),
                });
            }
        }
        flagColumns.sort((a, b) => b.score - a.score ||
            DnpColumnCandidateFinder.presentCount(b) - DnpColumnCandidateFinder.presentCount(a) ||
            a.column.localeCompare(b.column));
        markerColumns.sort((a, b) => b.markerCount - a.markerCount || a.column.localeCompare(b.column));
        return {
            moduleCount: metadataRows.length,
            columnNames: [...valueCounts.keys()].sort((a, b) => a.localeCompare(b)),
            flagColumns,
            markerColumns,
        };
    }
    static countValues(metadataRows) {
        const valueCounts = new Map();
        for (const metadata of metadataRows) {
            for (const [column, raw] of Object.entries(metadata ?? {})) {
                if (DnpColumnCandidateFinder.PARSER_KEY_PREFIXES.some((prefix) => column.startsWith(prefix))) {
                    continue;
                }
                const counts = valueCounts.get(column) ?? new Map();
                valueCounts.set(column, counts);
                const value = dnp_metadata_value_1.DnpMetadataValue.normalize(raw);
                if (value !== null) {
                    counts.set(value, (counts.get(value) ?? 0) + 1);
                }
            }
        }
        return valueCounts;
    }
    static scoreFlagColumn(column, values) {
        const compactName = column.toLowerCase().replace(/[^a-z0-9]/g, '');
        let score = 0;
        if (DnpColumnCandidateFinder.STRONG_NAME_PATTERN.test(compactName)) {
            score += 2;
        }
        else if (DnpColumnCandidateFinder.WEAK_NAME_PATTERN.test(compactName)) {
            score += 1;
        }
        if (values.some((value) => dnp_metadata_value_1.DnpMetadataValue.isMarker(value))) {
            score += 3;
        }
        else if (values.some((value) => DnpColumnCandidateFinder.FLAG_VALUES.has(value))) {
            score += 1;
        }
        return score;
    }
    static sortStates(counts) {
        return [...counts]
            .map(([value, count]) => ({ value, count }))
            .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
    }
    static presentCount(candidate) {
        return candidate.states.reduce((sum, state) => sum + state.count, 0);
    }
}
exports.DnpColumnCandidateFinder = DnpColumnCandidateFinder;
//# sourceMappingURL=dnp-column-candidate-finder.js.map